import React from "react";
import { jt, msgid, ngettext, t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../../Clickables";
import { Svg } from "../../Svg";
import { useProductContext } from "../ProductContext/ProductContext";
import { SuiteState, computePricing, discountRules } from "../engine";

import styles from "./SuiteSummary.module.scss";

/**
 * Suite-level price summary used by Populated Landing's status bar and
 * the Mini Cart footer. Always shows appliance count + subtotal; the
 * Instant Savings (with `ⓘ` info trigger) and total-with-savings pieces
 * appear only when the discount has been earned.
 *
 * Owns the off-white bar chrome (bg, padding, font, container-type) so
 * the two consumers don't duplicate it. Position-specific bits (border
 * side, grid placement, flex behavior in a panel) come in via
 * `className`. Trailing controls (email, print, etc.) plug in via
 * `actions`.
 */
type SuiteSummaryProps = {
    suite: SuiteState;
    onShowSavingsDetails: () => void;
    actions?: React.ReactNode;
    className?: string;
};

export const SuiteSummary = ({
    suite,
    onShowSavingsDetails,
    actions,
    className,
}: SuiteSummaryProps) => {
    const { catalog, locale } = useProductContext();
    const pricing = computePricing(suite, catalog, locale);
    const count = suite.contributions.length;
    const hasSavings = Number(pricing.savings.amount) > 0;
    const ratePct = Math.round(discountRules[0].discountRate * 100);
    const infoButton = (
        <Clickable
            key="info"
            className={styles.savingsInfo}
            onPress={onShowSavingsDetails}
        >
            <Svg
                name="info"
                className={styles.savingsInfoSvg}
                visuallyHiddenText={t`Savings details`}
            />
        </Clickable>
    );
    return (
        <div className={concatClassNames([styles.bar, className])}>
            <div className={styles.group}>
                <div>
                    {ngettext(
                        msgid`${count} appliance`,
                        `${count} appliances`,
                        count,
                    )}
                </div>
                <div aria-hidden="true" className={styles.spacer} />
                <div>{t`Total ${pricing.subtotal.formatted}`}</div>
                {hasSavings && (
                    <>
                        <div aria-hidden="true" className={styles.spacer} />
                        <div className={styles.instantSavings}>
                            {jt`Instant ${ratePct}% Savings ${infoButton} ${pricing.savings.formatted}`}
                        </div>
                        <div aria-hidden="true" className={styles.spacer} />
                        <div className={styles.totalWithSavings}>
                            {t`Total with savings ${pricing.total.formatted}`}
                        </div>
                    </>
                )}
            </div>
            {actions}
        </div>
    );
};
